import type {
  ApiJsonRequestBody,
  ApiMethodForPath,
  ApiPath,
  ApiPathParams,
  ApiQueryParams,
  ApiSuccessResponse
} from "./types";

const JSON_CONTENT_TYPE = "application/json";
const SERVER_API_BASE_URL =
  process.env.ALISTER_API_URL ?? "http://127.0.0.1:8000";
const DEFAULT_API_BASE_URL =
  typeof window === "undefined" ? SERVER_API_BASE_URL : "";

type Primitive = string | number | boolean;
type QueryItem = Primitive | null | undefined;
type QueryValue = QueryItem | QueryItem[];
type QueryRecord = Record<string, QueryValue>;
type PathParamRecord = Record<string, Primitive>;

export interface ApiClientOptions {
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  headers?: HeadersInit;
}

export interface ApiRequestOptions<
  Path extends ApiPath,
  Method extends ApiMethodForPath<Path>
> {
  pathParams?: ApiPathParams<Path, Method>;
  query?: ApiQueryParams<Path, Method>;
  body?: ApiJsonRequestBody<Path, Method>;
  init?: Omit<RequestInit, "body" | "method">;
}

export class ApiClientError extends Error {
  readonly status: number;
  readonly payload: unknown;

  constructor(status: number, payload: unknown, message: string) {
    super(message);
    this.name = "ApiClientError";
    this.status = status;
    this.payload = payload;
  }
}

function normalizeBaseUrl(baseUrl: string): string {
  return baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
}

function interpolatePath(pathTemplate: string, pathParams?: PathParamRecord): string {
  if (!pathParams) {
    return pathTemplate;
  }

  return pathTemplate.replace(/\{([^}]+)\}/g, (_, key: string) => {
    const value = pathParams[key];
    if (value === undefined || value === null) {
      throw new Error(`Missing required path parameter: ${key}`);
    }
    return encodeURIComponent(String(value));
  });
}

function appendQueryString(path: string, query?: QueryRecord): string {
  if (!query) {
    return path;
  }

  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) {
      continue;
    }

    const values = Array.isArray(value) ? value : [value];
    for (const item of values) {
      if (item === undefined || item === null) {
        continue;
      }
      searchParams.append(key, String(item));
    }
  }

  const serialized = searchParams.toString();
  if (!serialized) {
    return path;
  }

  return `${path}?${serialized}`;
}

function resolveUrl(baseUrl: string, path: string): string {
  if (!baseUrl || path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }

  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${baseUrl}${normalizedPath}`;
}

async function parseResponsePayload(response: Response): Promise<unknown> {
  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes(JSON_CONTENT_TYPE)) {
    return response.json();
  }

  const text = await response.text();
  return text.length > 0 ? text : null;
}

export interface ApiClient {
  request<Path extends ApiPath, Method extends ApiMethodForPath<Path>>(
    path: Path,
    method: Method,
    options?: ApiRequestOptions<Path, Method>
  ): Promise<ApiSuccessResponse<Path, Method>>;
}

export function createApiClient(options: ApiClientOptions = {}): ApiClient {
  const fetchImpl = options.fetchImpl ?? fetch;
  const baseUrl = normalizeBaseUrl(options.baseUrl ?? DEFAULT_API_BASE_URL);
  const defaultHeaders = options.headers ?? {};

  return {
    async request<Path extends ApiPath, Method extends ApiMethodForPath<Path>>(
      path: Path,
      method: Method,
      options: ApiRequestOptions<Path, Method> = {}
    ): Promise<ApiSuccessResponse<Path, Method>> {
      const pathWithParams = interpolatePath(
        path,
        options.pathParams as PathParamRecord | undefined
      );
      const pathWithQuery = appendQueryString(
        pathWithParams,
        options.query as QueryRecord | undefined
      );
      const url = resolveUrl(baseUrl, pathWithQuery);

      const headers = new Headers(defaultHeaders);
      const requestInit: RequestInit = {
        ...options.init,
        method: method.toUpperCase(),
        headers
      };

      if (options.body !== undefined) {
        if (!headers.has("Content-Type")) {
          headers.set("Content-Type", JSON_CONTENT_TYPE);
        }
        requestInit.body = JSON.stringify(options.body);
      }

      const response = await fetchImpl(url, requestInit);
      const payload = await parseResponsePayload(response);

      if (!response.ok) {
        // Redirect to login on 401 (expired/invalid session) for browser requests
        if (response.status === 401 && typeof window !== "undefined") {
          const currentPath = window.location.pathname + window.location.search;
          window.location.href = `/login?next=${encodeURIComponent(currentPath)}`;
          // Return a never-resolving promise so callers don't see an error flash
          return new Promise<never>(() => {});
        }

        throw new ApiClientError(
          response.status,
          payload,
          `API request failed: ${method.toUpperCase()} ${pathWithQuery}`
        );
      }

      return payload as ApiSuccessResponse<Path, Method>;
    }
  };
}

export const apiClient = createApiClient();
